'use client';
import { FaqBlock as IFaqBlock } from '../types';
import Heading from '../elements/Heading';
import { useState } from 'react';
import Link from 'next/link';
import TriangleIcon from '@/assets/icon/triangle-right-icon.svg';

export default function FaqBlock({ block }: { block: IFaqBlock }) {
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [openFaqs, setOpenFaqs] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);

  const toggleFaq = (faqId: string) => {
    const newOpenFaqs = new Set(openFaqs);
    if (newOpenFaqs.has(faqId)) {
      newOpenFaqs.delete(faqId);
    } else {
      newOpenFaqs.add(faqId);
    }
    setOpenFaqs(newOpenFaqs);
  };

  const questionsToShow = showAll
    ? block.faq_groups.flatMap((group, groupIdx) =>
        group.faqs.map((faq, faqIdx) => ({
          ...faq,
          groupIdx,
          faqIdx,
        })),
      )
    : block.faq_groups[selectedGroupIndex].faqs.map((faq, faqIdx) => ({
        ...faq,
        groupIdx: selectedGroupIndex,
        faqIdx,
      }));

  return (
    <div className="px-4 py-16">
      <div className="mx-auto max-w-6xl">
        {block.title && (
          <div className="mb-8">
            <Heading
              {...block.title}
              className="font-source-serif-pro text-[32px] leading-[130%] font-semibold tracking-[-0.014em]"
            />
          </div>
        )}
        <div className="flex flex-col gap-8 md:flex-row">
          {/* Sidebar */}
          <aside className="mb-8 md:mb-0 md:w-[260px]">
            <nav className="flex flex-col">
              {block.faq_groups.map((group, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedGroupIndex(idx);
                    setShowAll(false);
                  }}
                  className={`font-source-sans-pro cursor-pointer rounded py-3 text-left text-sm leading-6 font-semibold uppercase transition-colors ${!showAll && idx === selectedGroupIndex ? 'text-indigo-800' : 'text-graphite-700'} `}
                  dangerouslySetInnerHTML={{ __html: group.group_title }}
                ></button>
              ))}
            </nav>

            {block.see_all_cta && !showAll && (
              <div className="mt-8">
                <button
                  onClick={() => setShowAll(true)}
                  className="inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-teal-700 hover:underline"
                >
                  <span
                    dangerouslySetInnerHTML={{
                      __html: block.see_all_cta.see_all_text,
                    }}
                  />
                  <TriangleIcon
                    width={9}
                    height={9}
                    className="text-teal-700"
                  />
                </button>
              </div>
            )}

            {/* CTA Section */}
            {block.cta && (
              <div className="mt-8">
                {block.cta.text && (
                  <p
                    className="text-graphite-700 font-source-sans-pro mb-3 text-sm font-semibold"
                    dangerouslySetInnerHTML={{ __html: block.cta.text }}
                  />
                )}
                {block.cta.button_text && block.cta.button_url && (
                  <Link
                    href={block.cta.button_url}
                    className="inline-flex items-center gap-2 rounded border border-teal-700 bg-teal-700 px-6 py-2 text-sm font-medium text-white hover:bg-white hover:text-teal-700"
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: block.cta.button_text,
                      }}
                    />
                    <TriangleIcon
                      width={9}
                      height={9}
                      className="text-current transition-colors"
                    />
                  </Link>
                )}
              </div>
            )}
          </aside>
          {/* Main FAQ Area */}
          <div className="flex-1">
            <div className="flex w-full max-w-[760px] flex-col">
              {questionsToShow.map(faq => {
                const faqId = `${faq.groupIdx}-${faq.faqIdx}`;
                const isOpen = openFaqs.has(faqId);
                return (
                  <div
                    key={faqId}
                    className="border-b border-gray-200 last:border-b-0"
                  >
                    <button
                      onClick={() => toggleFaq(faqId)}
                      className="group flex w-full cursor-pointer justify-between py-4 text-left"
                    >
                      <span
                        className={`text-md text-graphite-700 max-w-[696px] font-serif font-normal`}
                        dangerouslySetInnerHTML={{ __html: faq.question }}
                      />
                      <span
                        className={`text-graphite-700 transition-transform duration-200 group-hover:text-gray-600 ${
                          isOpen ? 'rotate-90' : ''
                        }`}
                      >
                        <TriangleIcon
                          width={9}
                          height={9}
                          className="rotate-90"
                        />
                      </span>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div
                        className={`text-graphite-700 max-w-none pb-4 text-sm`}
                        dangerouslySetInnerHTML={{ __html: faq.answer }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
